import React, { useEffect, useMemo, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSite } from "../store/useSite";
import { getDashboardContextValue } from "../utils/dashboardContext";
import { useAuth } from "../store/useAuth";
import { AddHallModal } from "../components/AddHallModal";
import { getHalls, getTicketsForZone, createHall as createHallApi, getAllZones, updateZone, createZone, deleteZone } from "../services/zone";
import { useTimezoneStore } from "../store/useTimezoneStore";
import { formatDisplayDateTime, formatInTimezone, computeEventUtcInstant, getTimezoneAbbr } from "../utils/timezoneFormatter";

/* helpers */
const fmtDT = (iso, tz) => {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatDisplayDateTime(iso, targetTz);
};

const ROLES = [
  "Attendees",
  "Speakers",
  "Exhibitors",
  "Organizers",
  "Volunteers",
  "Sponsors",
];
const NAME_MAX = 255;
const ZONE_MODAL_TOAST_ID = "zone-modal-toast";
const zoneModalToastOptions = {
  containerId: ZONE_MODAL_TOAST_ID,
};

function toDateInputValue(iso, tz) {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatInTimezone(iso, targetTz, "YYYY-MM-DD");
}
function toTimeInputValue(iso, tz) {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatInTimezone(iso, targetTz, "HH:mm");
}
function combineLocal(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const instant = computeEventUtcInstant(dateStr, normalizedTime, targetTz);
  return instant ? instant.toISOString() : new Date(`${dateStr}T${timeStr}:00Z`).toISOString();
}

function buildAccessPayload({
  openToAll,
  allowAttendees,
  attendeeTicketScope,
  selectedTicketClassIds,
  allowSpeakers,
  allowSponsors,
  sponsorCategoryScope,
  selectedSponsorIds,
  allowExhibitors,
  boothCategoryScope,
  selectedExhibitorClassIds,
}) {
  if (openToAll) return [{ role: "all" }];

  const access = [];

  if (allowAttendees) {
    access.push(
      attendeeTicketScope === "all"
        ? { role: "attendee", scope: "all" }
        : {
          role: "attendee",
          scope: "selected",
          ticket_class_ids: selectedTicketClassIds,
        },
    );
  }
  if (allowSpeakers) {
    access.push({ role: "speaker", scope: "all" });
  }
  if (allowSponsors) {
    access.push(
      sponsorCategoryScope === "all"
        ? { role: "sponsor", scope: "all" }
        : {
          role: "sponsor",
          scope: "selected",
          sponsor_ids: selectedSponsorIds,
        },
    );
  }
  if (allowExhibitors) {
    access.push(
      boothCategoryScope === "all"
        ? { role: "exhibitor", scope: "all" }
        : {
          role: "exhibitor",
          scope: "selected",
          exhibitor_ticket_class_ids: selectedExhibitorClassIds,
        },
    );
  }
  return access;
}

/* modal */
export function ZoneModal({ open, onClose, onSave, initial, onCreateHall }) {
  const { eventData } = useSite();
  const eventTz = eventData?.timezone || "Asia/Kolkata";
  const eventTzAbbr = getTimezoneAbbr(eventTz);
  const [name, setName] = useState(initial?.name ?? "");
  const [location, setLocation] = useState(initial?.location ?? "");
  const [showLocationInput, setShowLocationInput] = useState(Boolean(initial?.location));

  // initial.opensAt / initial.closesAt are expected to be ISO UTC (we passed from ZonesPage)
  const [openDate, setOpenDate] = useState(() => toDateInputValue(initial?.opensAt, eventTz));
  const [openTime, setOpenTime] = useState(() => toTimeInputValue(initial?.opensAt, eventTz));
  const [closeDate, setCloseDate] = useState(() => toDateInputValue(initial?.closesAt, eventTz));
  const [closeTime, setCloseTime] = useState(() => toTimeInputValue(initial?.closesAt, eventTz));

  const [openToAll, setOpenToAll] = useState(true);
  const [access, setAccess] = useState([]);

  const [saving, setSaving] = useState(false);
  const [hallOpen, setHallOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  // group toggles
  const [allowAttendees, setAllowAttendees] = useState(true);
  const [allowSpeakers, setAllowSpeakers] = useState(true);
  const [allowSponsors, setAllowSponsors] = useState(false);
  const [allowExhibitors, setAllowExhibitors] = useState(false);
  const [allowMultipleEntry, setAllowMultipleEntry] = useState(Number(initial?.multiple_entry) === 1);
  const [requiredCheckOut, setRequiredCheckOut] = useState(Number(initial?.required_check_out) === 1);

  // halls
  const [halls, setHalls] = useState([]);
  const [hallLoading, setHallLoading] = useState(false);
  const [selectedHallId, setSelectedHallId] = useState(null);

  const eventId = getDashboardContextValue("eventId") || "";
  const userDetail = useAuth.getState().user;
  const token = userDetail ? userDetail?.token : "";

  // “selected vs all” scopes + selected IDs
  const [attendeeTicketScope, setAttendeeTicketScope] = useState("all");
  const [sponsorCategoryScope, setSponsorCategoryScope] = useState("all");
  const [boothCategoryScope, setBoothCategoryScope] = useState("all");

  const [selectedTicketClassIds, setSelectedTicketClassIds] = useState([]);
  const [selectedSponsorIds, setSelectedSponsorIds] = useState([]);
  const [selectedExhibitorClassIds, setSelectedExhibitorClassIds] = useState([]);

  // options loader (ticket classes, speakers, sponsors)
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [opts, setOpts] = useState([]);
  const [error, setError] = useState(null);

  const ticketClasses = useMemo(
    () =>
      opts.filter(
        (o) =>
          o.type === null ||
          o.type === "team" ||
          o.type === "exhibitor" ||
          o.type === "attendee"
      ),
    [opts]
  );
  const exhibitorTicketClasses = useMemo(
    () => opts.filter((o) => o.type === "exhibitor"),
    [opts]
  );
  const speakers = useMemo(() => opts.filter((o) => o.type === "speaker"), [opts]);
  const sponsors = useMemo(() => opts.filter((o) => o.type === "sponsor"), [opts]);

  // ---------- helpers ----------
  function parseAccessFromInitial(a) {
    // backend returns array like:
    // [{ role:'all' }] OR objects with role: 'attendee'|'speaker'|'sponsor'|'exhibitor',
    // plus scope 'all'|'selected' and selected arrays
    const arr = Array.isArray(a) ? a : [];
    const hasAll = arr.some((x) => x && x.role === "all");
    if (hasAll || arr.length === 0) {
      setOpenToAll(true);
      setAllowAttendees(true);
      setAllowSpeakers(true);
      setAllowSponsors(true);
      setAllowExhibitors(true);
      setAccess(ROLES.slice());
      // reset specifics
      setAttendeeTicketScope("all");
      setSponsorCategoryScope("all");
      setBoothCategoryScope("all");
      setSelectedTicketClassIds([]);
      setSelectedSponsorIds([]);
      setSelectedExhibitorClassIds([]);
      return;
    }

    setOpenToAll(false);

    // Defaults off, enable as present
    let att = false,
      spk = false,
      spn = false,
      exh = false;

    // defaults
    let attendeeScope = "all";
    let sponsorScope = "all";
    let exhibitorScope = "all";

    let attendeeIds = [];
    let sponsorIds = [];
    let exhibitorIds = [];

    arr.forEach((x) => {
      if (!x || typeof x !== "object") return;
      if (x.role === "attendee") {
        att = true;
        if (x.scope === "selected" && Array.isArray(x.ticket_class_ids)) {
          attendeeScope = "selected";
          attendeeIds = x.ticket_class_ids.slice();
        } else {
          attendeeScope = "all";
        }
      }
      if (x.role === "speaker") spk = true;
      if (x.role === "sponsor") {
        spn = true;
        if (x.scope === "selected" && Array.isArray(x.sponsor_ids)) {
          sponsorScope = "selected";
          sponsorIds = x.sponsor_ids.slice();
        } else {
          sponsorScope = "all";
        }
      }
      if (x.role === "exhibitor") {
        exh = true;
        if (x.scope === "selected" && Array.isArray(x.exhibitor_ticket_class_ids)) {
          exhibitorScope = "selected";
          exhibitorIds = x.exhibitor_ticket_class_ids.slice();
        } else {
          exhibitorScope = "all";
        }
      }
    });

    setAllowAttendees(att);
    setAllowSpeakers(spk);
    setAllowSponsors(spn);
    setAllowExhibitors(exh);

    const labels = [];
    if (att) labels.push("Attendees");
    if (spk) labels.push("Speakers");
    if (spn) labels.push("Sponsors");
    if (exh) labels.push("Exhibitors");
    setAccess(labels);

    setAttendeeTicketScope(attendeeScope);
    setSponsorCategoryScope(sponsorScope);
    setBoothCategoryScope(exhibitorScope);

    setSelectedTicketClassIds(attendeeIds);
    setSelectedSponsorIds(sponsorIds);
    setSelectedExhibitorClassIds(exhibitorIds);
  }

  // keep access label chips in sync with toggles / openToAll
  useEffect(() => {
    if (openToAll) {
      setAllowAttendees(true);
      setAllowSpeakers(true);
      setAllowSponsors(true);
      setAllowExhibitors(true);
      setAccess(ROLES.slice());
    } else {
      const arr = [];
      if (allowAttendees) arr.push("Attendees");
      if (allowSpeakers) arr.push("Speakers");
      if (allowSponsors) arr.push("Sponsors");
      if (allowExhibitors) arr.push("Exhibitors");
      setAccess(arr);
    }
  }, [openToAll, allowAttendees, allowSpeakers, allowSponsors, allowExhibitors]);

  // reset everything when opening
  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setLocation(initial?.location ?? "");
    setShowLocationInput(Boolean(initial?.location));

    setOpenDate(toDateInputValue(initial?.opensAt, eventTz));
    setOpenTime(toTimeInputValue(initial?.opensAt, eventTz));
    setCloseDate(toDateInputValue(initial?.closesAt, eventTz));
    setCloseTime(toTimeInputValue(initial?.closesAt, eventTz));

    // parse access config
    parseAccessFromInitial(initial?.access);

    // preselect hall (by id if present; else try name match after halls load)
    if (initial?.hall_id) {
      setSelectedHallId(String(initial.hall_id));
    } else if (initial?.location) {
      setSelectedHallId(null);
    }

    setAllowMultipleEntry(Number(initial?.multiple_entry) === 1);
    setRequiredCheckOut(Number(initial?.required_check_out) === 1);
  }, [open, initial]);

  // fetch halls
  const fetchHalls = async () => {
    try {
      setHallLoading(true);
      const res = await getHalls(eventId, token);
      const list =
        res.data?.data?.halls ||
        res.data?.data ||
        res.data?.halls ||
        res.data ||
        [];
      const arr = Array.isArray(list) ? list : [];
      setHalls(arr);

      // if no hall_id but we have a location, try name match
      if (!initial?.hall_id && initial?.location) {
        const m = arr.find(
          (h) => (h.name || "").toLowerCase() === initial.location.toLowerCase()
        );
        if (m) setSelectedHallId(String(m.id));
      }
    } catch (e) {
      toast.error("Failed to load halls", zoneModalToastOptions);
    } finally {
      setHallLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchHalls();
  }, [open]);

  // fetch access options (tickets/speakers/sponsors)
  useEffect(() => {
    let aborted = false;
    async function run() {
      if (!open || !eventId) return;
      setLoadingOpts(true);
      setError(null);
      try {
        const res = await getTicketsForZone(eventId, token);
        const json = res.data;
        const data = Array.isArray(json) ? json : json.data ?? [];
        if (!aborted) {
          setOpts(Array.isArray(data) ? data : []);
          // after options load, ensure selected IDs from initial.access remain set (no-op if already set)
          if (initial?.access) parseAccessFromInitial(initial.access);
        }
      } catch (e) {
        if (!aborted) {
          setError(e?.message || "Failed to load options");
          toast.error("Could not load access options", zoneModalToastOptions);
        }
      } finally {
        if (!aborted) setLoadingOpts(false);
      }
    }
    run();
    return () => {
      aborted = true;
    };
  }, [open, eventId, token]);

  if (!open) return null;

  const savingDisabled =
    !name.trim() || !openDate || !openTime || !closeDate || !closeTime;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <ToastContainer
        containerId={ZONE_MODAL_TOAST_ID}
        position="top-center"
        autoClose={3000}
        style={{ zIndex: 100000 }}
      />
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl my-4 sm:my-8 max-h-[85vh] flex flex-col">
        <div className="relative border-b px-4 sm:px-5 py-3 sm:py-4 sticky top-0 bg-white z-10">
          <div className="text-base sm:text-lg pr-8">
            {initial?.id ? "Edit Zone" : "Create Zone"}
          </div>
          <button
            aria-label="close"
            onClick={onClose}
            className="absolute right-3 sm:right-4 top-3 sm:top-4 text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid gap-4 sm:gap-5 px-4 sm:px-5 py-4 sm:py-5">
            {/* Zone name */}
            <div className="relative">
              <label className="mb-1 block text-sm text-gray-700">
                Zone Name <span className="text-red-600">*</span>
              </label>
              <input
                className="h-11 w-full rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                value={name}
                onChange={(e) =>
                  e.target.value.length <= NAME_MAX && setName(e.target.value)
                }
                placeholder="Zone Name"
              />
              <div className="pointer-events-none absolute right-2 top-0 text-xs text-gray-500">
                {name.length} / {NAME_MAX}
              </div>
            </div>

            {/* Hall / location */}
            <div>
              <div className="mb-1 text-sm text-gray-700">Location</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  className="h-11 w-full rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={selectedHallId ?? ""}
                  onChange={(e) => {
                    const id = e.target.value || null;
                    setSelectedHallId(id);
                    const found = halls.find((h) => String(h.id) === String(id));
                    setLocation(found ? found.name : "");
                  }}
                >
                  <option value="" disabled>
                    {hallLoading ? "Loading halls..." : "Select a hall"}
                  </option>
                  {halls.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                      {h.address ? ` — ${h.address}` : ""}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => setHallOpen(true)}
                  className="rounded border border-gray-300 px-3 py-2 sm:py-0 hover:bg-gray-50 whitespace-nowrap"
                >
                  Add Hall
                </button>
              </div>
            </div>

            {/* Date/Time */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-sm text-gray-700">
                  Zone Open Date {eventTzAbbr ? `(${eventTzAbbr})` : ""}
                </span>
                <input
                  type="date"
                  className="h-11 rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={openDate}
                  onChange={(e) => setOpenDate(e.target.value)}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-sm text-gray-700">
                  Zone Open Time {eventTzAbbr ? `(${eventTzAbbr})` : ""}
                </span>
                <input
                  type="time"
                  className="h-11 rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-sm text-gray-700">
                  Zone Close Date {eventTzAbbr ? `(${eventTzAbbr})` : ""}
                </span>
                <input
                  type="date"
                  className="h-11 rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={closeDate}
                  onChange={(e) => setCloseDate(e.target.value)}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-sm text-gray-700">
                  Zone Close Time {eventTzAbbr ? `(${eventTzAbbr})` : ""}
                </span>
                <input
                  type="time"
                  className="h-11 rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                />
              </label>
            </div>

            {/* Access cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setOpenToAll(true)}
                className={`rounded-lg border p-4 text-left ${openToAll ? "border-indigo-500 bg-indigo-50" : "border-gray-300 bg-white"
                  }`}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span
                    className={`inline-block h-4 w-4 rounded-full border flex-shrink-0 ${openToAll ? "border-indigo-600 bg-indigo-600" : "border-gray-400"
                      }`}
                  />
                  <span className="font-medium">Open to All</span>
                </div>
                <div className="text-sm text-gray-600">
                  Allow access to all participants in the event
                </div>
              </button>

              <button
                type="button"
                onClick={() => setOpenToAll(false)}
                className={`rounded-lg border p-4 text-left ${!openToAll ? "border-indigo-500 bg-indigo-50" : "border-gray-300 bg-white"
                  }`}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span
                    className={`inline-block h-4 w-4 rounded-full border flex-shrink-0 ${!openToAll ? "border-indigo-600 bg-indigo-600" : "border-gray-400"
                      }`}
                  />
                  <span className="font-medium">Open for specific groups</span>
                </div>
                <div className="text-sm text-gray-600">
                  Restrict access to a specific group like attendees, speakers, sponsors or exhibitors.
                </div>
              </button>
            </div>

            {/* Filters */}
            {!openToAll && (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className="mb-2 flex items-center gap-1 text-sm font-medium text-indigo-600"
                >
                  <span className={`inline-block transition-transform ${filtersOpen ? "rotate-0" : "-rotate-90"}`}>
                    ▾
                  </span>
                  More Filters
                </button>

                {filtersOpen && (
                  <div className="space-y-4 pl-1">
                    {/* Attendees */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 flex-shrink-0"
                          checked={allowAttendees}
                          onChange={(e) => setAllowAttendees(e.target.checked)}
                        />
                        <span className="font-medium">Allow Attendees</span>
                      </label>

                      <div
                        className={`flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-6 pl-7 ${!allowAttendees ? "text-gray-400" : ""
                          }`}
                      >
                        <label className={`inline-flex items-center gap-2 ${!allowAttendees ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="attendeeTicketScope"
                            value="selected"
                            checked={attendeeTicketScope === "selected"}
                            onChange={(e) => setAttendeeTicketScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>Selected Ticket Classes</span>
                        </label>

                        <label className={`inline-flex items-center gap-2 ${!allowAttendees ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="attendeeTicketScope"
                            value="all"
                            checked={attendeeTicketScope === "all"}
                            onChange={(e) => setAttendeeTicketScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>All Ticket Classes</span>
                        </label>
                      </div>

                      {allowAttendees && attendeeTicketScope === "selected" && (
                        <div className="pl-7">
                          {loadingOpts && <div className="text-sm text-gray-500">Loading ticket classes…</div>}
                          {error && <div className="text-sm text-red-600">{error}</div>}
                          {!loadingOpts && !error && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                              {ticketClasses.map((t) => (
                                <label key={`t-${t.id}`} className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedTicketClassIds.includes(t.id)}
                                    onChange={(e) => {
                                      setSelectedTicketClassIds((prev) =>
                                        e.target.checked ? [...prev, t.id] : prev.filter((x) => x !== t.id)
                                      );
                                    }}
                                    className="flex-shrink-0"
                                  />
                                  <span className="truncate">{t.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Speakers (no “selected” mode in current backend) */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 flex-shrink-0"
                          checked={allowSpeakers}
                          onChange={(e) => setAllowSpeakers(e.target.checked)}
                        />
                        <span className="font-medium">Allow Speakers</span>
                      </label>
                      {allowSpeakers && (
                        <div className="pl-7 text-sm text-gray-600">
                          {loadingOpts ? "Loading speakers…" : `${speakers.length} speakers found`}
                        </div>
                      )}
                    </div>

                    {/* Sponsors */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 flex-shrink-0"
                          checked={allowSponsors}
                          onChange={(e) => setAllowSponsors(e.target.checked)}
                        />
                        <span className="font-medium">Allow Sponsors</span>
                      </label>

                      <div
                        className={`flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-6 pl-7 ${!allowSponsors ? "text-gray-400" : ""
                          }`}
                      >
                        <label className={`inline-flex items-center gap-2 ${!allowSponsors ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="sponsorCategoryScope"
                            value="selected"
                            checked={sponsorCategoryScope === "selected"}
                            onChange={(e) => setSponsorCategoryScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>Selected Sponsors</span>
                        </label>

                        <label className={`inline-flex items-center gap-2 ${!allowSponsors ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="sponsorCategoryScope"
                            value="all"
                            checked={sponsorCategoryScope === "all"}
                            onChange={(e) => setSponsorCategoryScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>All sponsors</span>
                        </label>
                      </div>

                      {allowSponsors && sponsorCategoryScope === "selected" && (
                        <div className="pl-7">
                          {loadingOpts && <div className="text-sm text-gray-500">Loading sponsors…</div>}
                          {error && <div className="text-sm text-red-600">{error}</div>}
                          {!loadingOpts && !error && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                              {sponsors.map((s) => (
                                <label key={`s-${s.id}`} className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedSponsorIds.includes(s.id)}
                                    onChange={(e) => {
                                      setSelectedSponsorIds((prev) =>
                                        e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                                      );
                                    }}
                                    className="flex-shrink-0"
                                  />
                                  <span className="truncate">{s.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Exhibitors */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 flex-shrink-0"
                          checked={allowExhibitors}
                          onChange={(e) => setAllowExhibitors(e.target.checked)}
                        />
                        <span className="font-medium">Allow Exhibitors</span>
                      </label>

                      <div
                        className={`flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-6 pl-7 ${!allowExhibitors ? "text-gray-400" : ""
                          }`}
                      >
                        <label className={`inline-flex items-center gap-2 ${!allowExhibitors ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="boothCategoryScope"
                            value="selected"
                            checked={boothCategoryScope === "selected"}
                            onChange={(e) => setBoothCategoryScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>Selected Booth Types</span>
                        </label>

                        <label className={`inline-flex items-center gap-2 ${!allowExhibitors ? "pointer-events-none" : ""}`}>
                          <input
                            type="radio"
                            name="boothCategoryScope"
                            value="all"
                            checked={boothCategoryScope === "all"}
                            onChange={(e) => setBoothCategoryScope(e.target.value)}
                            className="flex-shrink-0"
                          />
                          <span>All Booth Types</span>
                        </label>
                      </div>

                      {allowExhibitors && boothCategoryScope === "selected" && (
                        <div className="pl-7">
                          {loadingOpts && <div className="text-sm text-gray-500">Loading booth types…</div>}
                          {error && <div className="text-sm text-red-600">{error}</div>}
                          {!loadingOpts && !error && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                              {exhibitorTicketClasses.map((ecl) => (
                                <label key={`e-${ecl.id}`} className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedExhibitorClassIds.includes(ecl.id)}
                                    onChange={(e) => {
                                      setSelectedExhibitorClassIds((prev) =>
                                        e.target.checked ? [...prev, ecl.id] : prev.filter((x) => x !== ecl.id)
                                      );
                                    }}
                                    className="flex-shrink-0"
                                  />
                                  <span className="truncate">{ecl.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 rounded-xl border">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900">Multiple Entry</div>
                <div className="inline-flex items-center gap-2 select-none">
                  <span className="text-sm text-gray-500">Off</span>
                  <button
                    type="button"
                    aria-label="Multiple entry"
                    aria-pressed={allowMultipleEntry}
                    onClick={() => {
                      const next = !allowMultipleEntry;
                      setAllowMultipleEntry(next);
                      if (!next) setRequiredCheckOut(false);
                    }}
                    className={`${allowMultipleEntry ? "bg-blue-600" : "bg-gray-300"} w-12 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600`}
                  >
                    <span className={`absolute top-1 left-1 h-4 w-4 bg-white rounded-full transition-transform ${allowMultipleEntry ? "translate-x-6" : ""}`} />
                  </button>
                  <span className="text-sm text-gray-500">On</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Allow attendees to check in multiple times. Scan screen shows last check-in.</p>
            </div>

            <div className="p-4 rounded-xl border">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900">Required Check Out</div>
                <div className="inline-flex items-center gap-2 select-none">
                  <span className="text-sm text-gray-500">Off</span>
                  <button
                    type="button"
                    aria-label="Required check out"
                    disabled={!allowMultipleEntry}
                    aria-pressed={requiredCheckOut}
                    onClick={() => setRequiredCheckOut(!requiredCheckOut)}
                    className={`${requiredCheckOut ? "bg-blue-600" : "bg-gray-300"} ${!allowMultipleEntry ? "opacity-40 cursor-not-allowed" : ""} w-12 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600`}
                  >
                    <span className={`absolute top-1 left-1 h-4 w-4 bg-white rounded-full transition-transform ${requiredCheckOut ? "translate-x-6" : ""}`} />
                  </button>
                  <span className="text-sm text-gray-500">On</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Requiring Check-out before Check-in</p>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t px-4 sm:px-5 py-4 sticky bottom-0 bg-white">
          <button className="h-10 rounded border border-gray-300 px-4" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="h-10 rounded bg-indigo-600 px-6 text-white hover:bg-indigo-700 disabled:opacity-60"
            onClick={async () => {
              if (savingDisabled) {
                if (!name.trim()) toast.error("zone name is required", zoneModalToastOptions);
                else toast.error("open/close date and time required", zoneModalToastOptions);
                return;
              }
              if (!selectedHallId) {
                toast.error("please select a hall", zoneModalToastOptions);
                return;
              }
              try {
                setSaving(true);
                const opensAtISO = combineLocal(openDate, openTime, eventTz);
                const closesAtISO = combineLocal(closeDate, closeTime, eventTz);

                const accessPayload = buildAccessPayload({
                  openToAll,
                  allowAttendees,
                  attendeeTicketScope,
                  selectedTicketClassIds,
                  allowSpeakers,
                  allowSponsors,
                  sponsorCategoryScope,
                  selectedSponsorIds,
                  allowExhibitors,
                  boothCategoryScope,
                  selectedExhibitorClassIds,
                });

                await onSave({
                  id: initial?.id,
                  name: name.trim(),
                  hall_id: selectedHallId ? parseInt(selectedHallId, 10) : null,
                  opensAt: opensAtISO,
                  closesAt: closesAtISO,
                  access: accessPayload,
                  multiple_entry: allowMultipleEntry ? 1 : 0,
                  required_check_out: requiredCheckOut ? 1 : 0,
                });
                onClose();
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <AddHallModal
        open={hallOpen}
        onClose={() => setHallOpen(false)}
        onSave={async ({ name, address }) => {
          const created = await onCreateHall({ name, address });
          await fetchHalls();
          const id = String(created?.id || "");
          if (id) {
            setSelectedHallId(id);
            setLocation(created.name || "");
          }
          return created;
        }}
      />
    </div>
  );
}


/* delete confirmation */
function ConfirmDelete({ open, onClose, onConfirm, title, message }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="px-4 sm:px-5 pt-4 sm:pt-5 text-base sm:text-lg">{title}</div>
        <div className="px-4 sm:px-5 pb-4 pt-3 text-sm sm:text-base text-gray-700">{message}</div>
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 border-t px-4 sm:px-5 py-4">
          <button
            className="h-9 rounded border border-gray-300 px-4"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="h-9 rounded bg-red-600 px-4 text-white hover:bg-red-700 disabled:opacity-60"
            onClick={async () => {
              try {
                setBusy(true);
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* main page */
function ZonesPage() {
  const { eventData, setEventData } = useSite();
  const eventTz = eventData?.timezone || "Asia/Kolkata";
  const { getActiveTimezone } = useTimezoneStore();
  const activeTz = getActiveTimezone(eventTz);
  const [zones, setZones] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("All");

  const [dirty, setDirty] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [delOpen, setDelOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [activeMenu, setActiveMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const userDetail = useAuth.getState().user;
  const userData = userDetail ? userDetail : null;
  const eventId = getDashboardContextValue("eventId") || "";

  // ----- helpers -----
  const authHeaders = {
    Authorization: `Bearer ${userData?.token ?? ""}`,
    "ngrok-skip-browser-warning": "true",
  };

  // normalize API payload to what UI expects
  function normalizeZone(api) {
    if (!api) return null;
    const access =
      Array.isArray(api.access)
        ? api.access
        : (() => {
          try {
            const p = typeof api.access === "string" ? JSON.parse(api.access) : api.access;
            return Array.isArray(p) ? p : [];
          } catch {
            return [];
          }
        })();

    return {
      // ids / keys
      id: api.id ?? api.zone_id ?? api._id ?? api.Id,
      event_id: api.event_id,

      // names / location
      name: api.name ?? "",
      // location is not persisted on zone table; show hall name if your list provides it, else string fallback
      location: api.location ?? api.hall_name ?? "",

      hall_id: api.hall_id ?? null,

      // for display — prefer UTC timestamp so fmtDT can format in active timezone
      opens_at: api.opens_at_utc ?? api.opens_at ?? api.opensAt ?? api.opens_at_ist ?? null,
      closes_at: api.closes_at_utc ?? api.closes_at ?? api.closesAt ?? api.closes_at_ist ?? null,

      // for editing (UTC)
      opens_at_utc: api.opens_at_utc ?? api.opens_at ?? api.opensAt ?? null,
      closes_at_utc: api.closes_at_utc ?? api.closes_at ?? api.closesAt ?? null,

      // access array
      access,
      multiple_entry: Number(api.multiple_entry) === 1 ? 1 : 0,
      required_check_out: Number(api.required_check_out) === 1 ? 1 : 0,
    };
  }

  const createHall = async ({ name, address }) => {
    const res = await createHallApi({ name, address, event_id: eventId }, userData?.token);
    return (
      res.data?.data || {
        id: Math.random().toString(36).slice(2),
        name,
        address,
      }
    );
  };

  // initial load
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAllZones(eventId, userData?.token);

        // support multiple server shapes:
        // 1) { success:true, zones:[...] }
        // 2) { success:true, data:{ zones:[...] } }
        // 3) { zones:[...] }
        const apiZones =
          res.data?.zones ??
          res.data?.data?.zones ??
          res.data?.data ??
          [];

        const normalized = (Array.isArray(apiZones) ? apiZones : []).map(normalizeZone);
        setZones(normalized);
      } catch (e) {
        toast.error("failed to load zones");
      }
    };
    load();
  }, []);

  function formatAccessItem(item) {
    if (!item || typeof item !== "object") return "";
    const roleLabel =
      item.role === "attendee"
        ? "Attendees"
        : item.role === "speaker"
          ? "Speakers"
          : item.role === "sponsor"
            ? "Sponsors"
            : item.role === "exhibitor"
              ? "Exhibitors"
              : String(item.role || "");

    if (item.scope === "all") return `${roleLabel} (all)`;

    if (item.scope === "selected") {
      const count = Array.isArray(item.ticket_class_ids)
        ? item.ticket_class_ids.length
        : Array.isArray(item.sponsor_ids)
          ? item.sponsor_ids.length
          : Array.isArray(item.exhibitor_ticket_class_ids)
            ? item.exhibitor_ticket_class_ids.length
            : 0;
      return `${roleLabel} (${count} selected)`;
    }

    if (item.role === "all") return "Open to All";
    return roleLabel;
  }

  function formatAccessSummary(accessArray) {
    if (!Array.isArray(accessArray)) return [];
    return accessArray.map(formatAccessItem).filter(Boolean);
  }

  // filtering
  useEffect(() => {
    const x = zones.filter((z) => {
      const hit =
        (z?.name || "")?.toLowerCase()?.includes(search?.toLowerCase()) ||
        (z?.location || "")?.toLowerCase()?.includes(search?.toLowerCase());
      const loc =
        locationFilter === "All" ||
        (z?.location || "").toLowerCase() === locationFilter?.toLowerCase();
      return hit && loc;
    });
    setFiltered(x);
  }, [zones, search, locationFilter]);

  const distinctLocations = useMemo(() => {
    const set = new Set(zones?.map((z) => z?.location).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [zones]);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (z) => {
    // ensure we pass UTC values back into the modal for accurate recompute
    setEditing({
      ...z,
      opensAt: z.opens_at_utc ?? z.opens_at ?? null,
      closesAt: z.closes_at_utc ?? z.closes_at ?? null,
    });
    setModalOpen(true);
  };

  const saveZone = async (payload) => {
    try {
      if (payload.id) {
        // UPDATE
        const res = await updateZone(
          payload.id,
          eventId,
          {
            name: payload.name,
            hall_id: payload.hall_id,
            opensAt: payload.opensAt, // ISO UTC
            closesAt: payload.closesAt, // ISO UTC
            access: payload.access,
            multiple_entry: payload.multiple_entry ?? 0,
            required_check_out: payload.required_check_out ?? 0,
          },
          userData?.token,
        );

        // server may return:
        // { success:true, data:{ zone:{...} } } or { success:true, zone:{...} }
        const returned =
          res.data?.data?.zone ?? res.data?.zone ?? res.data?.data ?? null;

        const updated = normalizeZone(returned) ?? {
          // fallback if server didn’t echo
          ...payload,
          opens_at: payload.opensAt,
          closes_at: payload.closesAt,
          multiple_entry: payload.multiple_entry ?? 0,
          required_check_out: payload.required_check_out ?? 0,
        };

        setZones((prev) => prev.map((z) => (z.id === payload.id ? { ...z, ...updated } : z)));
        toast.success("zone updated");
      } else {
        // CREATE
        const res = await createZone(
          eventId,
          {
            name: payload.name,
            hall_id: payload.hall_id,
            opensAt: payload.opensAt, // ISO UTC
            closesAt: payload.closesAt, // ISO UTC
            access: payload.access,
            multiple_entry: payload.multiple_entry ?? 0,
            required_check_out: payload.required_check_out ?? 0,
          },
          userData?.token,
        );

        // server returns { success:true, data:{ message, zone } }
        const created =
          res.data?.data?.zone ??
          res.data?.zone ??
          res.data?.data ??
          null;

        const newZone = normalizeZone(created) ?? {
          ...payload,
          id: Math.random().toString(36).slice(2),
          opens_at: payload.opensAt,
          closes_at: payload.closesAt,
          multiple_entry: payload.multiple_entry ?? 0,
          required_check_out: payload.required_check_out ?? 0,
        };

        setZones((prev) => [newZone, ...prev]);
        toast.success("zone added");
      }
      setDirty(true);
    } catch (e) {
      toast.error("could not save zone", zoneModalToastOptions);
      throw e;
    }
  };

  const askDelete = (z) => {
    setToDelete(z);
    setDelOpen(true);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      // align with backend deleteZone: DELETE /zone/deleteZone/:id?event_id=
      await deleteZone(toDelete.id, eventId, userData?.token);
      setZones((prev) => prev.filter((x) => x.id !== toDelete.id));
      setDirty(true);
      toast.success("zone deleted");
    } catch {
      toast.error("could not delete zone");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap"
      />
      <div
        className="mx-auto max-w-6xl px-3 sm:px-4 pb-8 sm:pb-14 pt-3 sm:pt-4"
        style={{
          fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        }}
      >
        <div className="mb-3 text-xl sm:text-2xl">Zones</div>

        <div className="mb-4 sm:mb-5 flex items-start gap-2 sm:gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-xs sm:text-sm text-slate-700">
          <div className="mt-0.5 flex-shrink-0">ℹ️</div>
          <div>
            Manage zones in your event here. To check in event participants into enter zones during the event, go to the{" "}
            <button className="underline" onClick={() => toast.info("Event Day tab")}>
              Event Day
            </button>{" "}
            tab.
          </div>
        </div>

        <div className="mb-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <div className="relative flex-1 min-w=[200px] max-w-full sm:max-w-[300px]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded border border-gray-300 pl-9 pr-3 outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 text-sm"
              placeholder="Search Zones"
            />
            <span className="pointer-events-none absolute left-3 top-2.5 text-gray-400">🔎</span>
          </div>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="h-10 rounded border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          >
            {distinctLocations.map((l) => (
              <option key={l} value={l}>
                {l === "All" ? "All Locations" : l}
              </option>
            ))}
          </select>

          <div className="sm:ml-auto" />
          <button
            onClick={openAdd}
            className="flex h-10 items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3 text-sm hover:bg-gray-50"
          >
            ＋ Add New Zone
          </button>
        </div>

        <div className="overflow-hidden rounded-md border border-gray-200 bg-white min-h-[70vh]">
          <div className="hidden lg:grid grid-cols-12 border-b bg-gray-50 px-4 py-3 text-sm text-gray-600">
            <div className="col-span-3">Zone Name</div>
            <div className="col-span-3">Location</div>
            <div className="col-span-4">Access Time</div>
            <div className="col-span-2">Access</div>
          </div>

          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm sm:text-base text-gray-500">no zones yet</div>
          )}

          {filtered.map((z) => (
            <div key={z.id} className="border-b last:border-b-0 px-3 sm:px-4 py-4 hover:bg-gray-50">
              <div className="lg:grid lg:grid-cols-12 lg:items-center lg:gap-4 space-y-3 lg:space-y-0">
                <div className="lg:col-span-3">
                  <div className="text-xs sm:text-sm text-gray-500 lg:hidden mb-1">Zone Name</div>
                  <div className="font-medium text-sm sm:text-base text-gray-900">{z.name}</div>
                </div>

                <div className="lg:col-span-3">
                  <div className="text-xs sm:text-sm text-gray-500 lg:hidden mb-1">Location</div>
                  <div className="flex items-center gap-2 text-sm sm:text-base">
                    <span>📍</span>
                    <span>{z.location || "—"}</span>
                  </div>
                </div>

                <div className="lg:col-span-4">
                  <div className="text-xs sm:text-sm text-gray-500 lg:hidden mb-1">Access Time</div>
                  <div className="text-xs sm:text-sm space-y-1">
                    <div>Opens At - {fmtDT(z.opens_at, activeTz)}</div>
                    <div>Closes At - {fmtDT(z.closes_at, activeTz)}</div>
                  </div>
                </div>

                <div className="lg:col-span-2 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-start sm:items-center lg:items-start xl:items-center justify-between gap-3">
                  <div>
                    <div className="text-xs sm:text-sm text-gray-500 lg:hidden mb-1">Access</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {(() => {
                        const labels = formatAccessSummary(z.access);
                        return (
                          <>
                            {labels.slice(0, 2).map((text, idx) => (
                              <span key={idx} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
                                {text}
                              </span>
                            ))}
                            {labels.length > 2 && (
                              <span className="rounded-full border px-2.5 py-0.5 text-xs text-gray-700">
                                {labels.length - 2}+
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <button className="h-8 rounded border border-gray-300 px-3 text-sm hover:bg-gray-50" onClick={() => openEdit(z)}>
                      Edit
                    </button>

                    <div className="relative" ref={activeMenu === z.id ? menuRef : null}>
                      <button
                        className="h-8 w-8 rounded border border-gray-300 text-lg leading-none hover:bg-gray-50"
                        onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === z.id ? null : z.id); }}
                      >
                        ⋮
                      </button>
                      {activeMenu === z.id && (
                        <div className="absolute right-0 z-50 mt-1 w-36 rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
                          <button className="block w-full px-3 py-1.5 text-left hover:bg-gray-50" onClick={() => { setActiveMenu(null); openEdit(z); }}>
                            Edit
                          </button>
                          <button
                            className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-gray-50"
                            onClick={() => { setActiveMenu(null); setToDelete(z); setDelOpen(true); }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <ZoneModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={saveZone} initial={editing} onCreateHall={createHall} />

        <ConfirmDelete
          open={delOpen}
          onClose={() => setDelOpen(false)}
          onConfirm={doDelete}
          title="Delete zone?"
          message={`This will remove "${toDelete?.name ?? ""}" from your event.`}
        />
      </div>
    </div>
  );
}

export default ZonesPage;
